import { UtmPayload } from './types';

const UTM_STORAGE_KEY = 'wl_utm_tags';

type StoredUtmParams = Partial<Record<keyof UtmPayload, string>>;

const UTM_QUERY_PARAM_MAP: Record<string, keyof UtmPayload> = {
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_medium: 'utmMedium',
  utm_source: 'utmSource',
  utm_term: 'utmTerm'
};

const parseSearchParams = (search: string): StoredUtmParams => {
  const params = new URLSearchParams(search);
  const result: StoredUtmParams = {};

  Object.entries(UTM_QUERY_PARAM_MAP).forEach(([queryKey, payloadKey]) => {
    const value = params.get(queryKey)?.trim();
    if (value) {
      result[payloadKey] = value;
    }
  });

  return result;
};

export const captureUtmParams = (searchQuery?: string): void => {
  if (typeof window === 'undefined') return;

  try {
    const search = searchQuery !== undefined ? searchQuery : window.location.search;
    if (!search) return;

    const parsed = parseSearchParams(search);
    if (Object.keys(parsed).length === 0) return;

    let existing: StoredUtmParams = {};
    const raw = window.localStorage.getItem(UTM_STORAGE_KEY);
    if (raw) {
      try {
        existing = JSON.parse(raw);
      } catch {
        existing = {};
      }
    }

    const merged: StoredUtmParams = {
      ...existing,
      ...parsed
    };

    window.localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    return;
  }
};

export const getStoredUtmParams = (): UtmPayload => {
  let stored: StoredUtmParams = {};

  if (typeof window !== 'undefined') {
    try {
      const raw = window.localStorage.getItem(UTM_STORAGE_KEY);
      if (raw) {
        stored = JSON.parse(raw);
      }
    } catch {
      stored = {};
    }

    if (Object.keys(stored).length === 0 && window.location.search) {
      stored = parseSearchParams(window.location.search);
    }
  }

  return {
    utmCampaign: stored.utmCampaign ?? null,
    utmContent: stored.utmContent ?? null,
    utmMedium: stored.utmMedium ?? null,
    utmSource: stored.utmSource ?? null,
    utmTerm: stored.utmTerm ?? null
  };
};

export const clearStoredUtmParams = (): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(UTM_STORAGE_KEY);
  } catch {
    return;
  }
};